<template>
  <section class="bg-[#fcf5ec] py-16">
    <div class="w-full grid lg:grid-cols-[2fr_1fr] gap-0 bg-[#EEDCC5]">
      <!-- FORM -->
      <div class="p-12 lg:p-16">
        <h2 class="text-[38px] font-black italic uppercase text-[#111] mb-10">{{ t('titles.resetPassword') }}</h2>

        <form @submit.prevent="handleSubmit" class="space-y-5 max-w-[760px]">
          <!-- New password -->
          <div class="max-w-[520px]">
            <div class="relative">
              <input ref="pass1" v-model="password" :type="showPass1 ? 'text' : 'password'" autocomplete="new-password" required
                     :placeholder="t('placeholders.newPassword')"
                     class="w-full h-12 bg-transparent px-1 pr-12 border-0 border-b-2 border-[#d4cfc5] text-black placeholder:text-[#8c857a]
                    focus:outline-none focus:border-black" />
              <button type="button" @click="togglePass1" @mousedown.prevent @touchstart.prevent
                      class="absolute right-0 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center text-[#666] hover:text-[#111] focus:outline-none"
                      :title="showPass1 ? t('aria.hidePassword') : t('aria.showPassword')"
                      :aria-pressed="showPass1">
                <svg v-if="showPass1" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a21.77 21.77 0 0 1 5.06-5.94"/>
                  <path d="M1 1l22 22"/>
                  <path d="M10.58 10.58a3 3 0 0 0 4.24 4.24"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Confirm -->
          <div class="max-w-[520px]">
            <div class="relative">
              <input ref="pass2" v-model="password2" :type="showPass2 ? 'text' : 'password'" autocomplete="new-password" required
                     :placeholder="t('placeholders.confirmPassword')"
                     class="w-full h-12 bg-transparent px-1 pr-12 border-0 border-b-2 border-[#d4cfc5] text-black placeholder:text-[#8c857a]
                    focus:outline-none focus:border-black" />
              <button type="button" @click="togglePass2" @mousedown.prevent @touchstart.prevent
                      class="absolute right-0 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center text-[#666] hover:text-[#111] focus:outline-none"
                      :title="showPass2 ? t('aria.hidePassword') : t('aria.showPassword')"
                      :aria-pressed="showPass2">
                <svg v-if="showPass2" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a21.77 21.77 0 0 1 5.06-5.94"/>
                  <path d="M1 1l22 22"/>
                  <path d="M10.58 10.58a3 3 0 0 0 4.24 4.24"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Submit -->
          <button type="submit" :disabled="isLoading || !formValid"
                  class="w-full max-w-[520px] h-12 bg-black text-white italic uppercase font-semibold tracking-wide disabled:opacity-60">
            <span v-if="isLoading">{{ t('buttons.sending') }}</span><span v-else>{{ t('buttons.savePassword') }}</span>
          </button>

          <!-- Messages -->
          <MmFlashMessage
            v-if="errorMessage"
            :message="errorMessage"
            type="error"
            @dismiss="errorMessage = ''"
          />
          <MmFlashMessage
            v-if="successMessage"
            :message="successMessage"
            type="success"
            :autoHideMs="6000"
            @dismiss="successMessage = ''"
          />

          <div class="w-full max-w-[520px] flex items-center justify-between -mt-2">
            <button type="button" @click="goToLogin" class="text-left p-0 text-[15px] text-[#111] underline italic hover:text-black bg-transparent">
              {{ t('buttons.backToLogin') }}
            </button>
          </div>
        </form>
      </div>

      <!-- INFO PANEL -->
      <aside class="bg-gradient-to-br from-[#EA6730] to-[#F7941F] text-white p-10 lg:p-14 flex flex-col gap-8">
        <div>
          <h3 class="text-[26px] text-white italic uppercase mb-6 leading-tight">
            <span class="whitespace-pre-line">{{ t('marketing.pitchTitle') }}</span>
          </h3>
          <ul class="space-y-4 text-[16px]">
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp1') }}</span>
            </li>
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp2') }}</span>
            </li>
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp3') }}</span>
            </li>
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp4') }}</span>
            </li>
          </ul>
        </div>
        <button @click="goToLogin" class="mt-4 w-full bg-white text-black italic uppercase font-extrabold h-12">
          {{ t('buttons.login') }}
        </button>
      </aside>
    </div>
  </section>
</template>

<script>
import WpApiClient from '../lib/wp-api.js'
import { t as i18nT } from '../i18n'
import MmFlashMessage from './MmFlashMessage.vue'
export default {
  name: 'MmResetPassword',
  emits: ['backToLogin'],
  components: { MmFlashMessage },
  data(){
    return {
      t: i18nT,
      login: '',
      key: '',
      password: '',
      password2: '',
      showPass1: false,
      showPass2: false,
      isLoading: false,
      errorMessage: '',
      successMessage: ''
    }
  },
  mounted(){
    const params = new URLSearchParams(window.location.search);
    this.login = params.get('login') || '';
    this.key = params.get('key') || '';
    this.api = new WpApiClient();
  },
  computed:{
    formValid(){
      const pOk = typeof this.password === 'string' && this.password.length >= 12;
      const match = pOk && this.password2 === this.password;
      const hasKey = !!this.login && !!this.key;
      return pOk && match && hasKey;
    }
  },
  methods:{
    t(key){ return i18nT(key) },
    togglePass1(){ this.showPass1 = !this.showPass1; },
    togglePass2(){ this.showPass2 = !this.showPass2; },
    scrollToRef(refName){
      const el = this.$refs[refName];
      if (el && typeof el.scrollIntoView === 'function') {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(_) { el.scrollIntoView(); }
      }
      if (el && typeof el.focus === 'function') {
        try { el.focus({ preventScroll: true }); } catch(_) { try { el.focus(); } catch(__) {} }
      }
    },
    findFirstInvalid(){
      if (!this.password || this.password.length < 12) return 'pass1';
      if (!this.password2 || this.password2 !== this.password) return 'pass2';
      return '';
    },
    async handleSubmit(){
      if (!this.formValid){
        const ref = this.findFirstInvalid();
        if (ref) this.scrollToRef(ref);
        return;
      }
      this.isLoading = true; this.errorMessage=''; this.successMessage='';
      try{
        const result = await this.api.resetPassword({
          login: this.login,
          key: this.key,
          newPassword: this.password,
          nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined,
        });
        if (result && result.ok){
          // Update nonce voor volgende verzoeken (zoals login)
          if (result.newNonce && window.materiaalAjax) {
            window.materiaalAjax.nonce = result.newNonce;
          }
          this.successMessage = this.t('messages.passwordSaved');
        } else {
          this.errorMessage = (result && result.error) || this.t('messages.submitFailed');
        }
      }catch(e){ this.errorMessage = e?.message || this.t('messages.genericError'); }
      this.isLoading = false;
    },
    goToLogin(){ this.$emit('backToLogin'); }
  }
}
</script>
